import { useState } from 'react';

import { viewApi } from '@/api/services/viewApi';
import { CategoryDropdown } from '@/components/inputs/CategoryDropdown';
import { EnumDropdown } from '@/components/inputs/EnumDropdown';
import { CheckBox, TabPanel, Tabs, TextBox, type TabItem } from '@/components/ui';
import { createChinaAppSysView, type ChinaAppSysView } from '@/types/entities';
import { EntityTypes, entityTypeDisplayNames } from '@/types/enums';

import { useAdminChanges } from '../adminChanges';
import { AdminNotice, AdminWarnings } from '../AdminNotice';
import { AdminViewFieldsList } from '../lists/AdminViewFieldsList';
import { AdminViewGroupsList } from '../lists/AdminViewGroupsList';
import {
  ADDITIONAL_FUNCTION_HINTS,
  ADDITIONAL_FUNCTIONS,
  buildAdditionalFunctions,
  checkDefaultFilter,
  checkDefaultOrder,
  parseAdditionalFunctions,
  type AdditionalFunction,
} from '../viewExpressions';
import { AdminFormFrame } from './AdminFormFrame';
import { useAdminForm } from './useAdminForm';

/** Formular einer View in drei Reitern — Port von `IwmAdminViewForm.razor` (REQ-29 §2.2). */
export interface AdminViewFormProps {
  id: number;
  onSave: (id: number) => void | Promise<void>;
}

const TABS_ID = 'iwm-admin-view';

type TabId = 'einstellungen' | 'felder' | 'gruppen';

/** Die Texte stehen so in REQ-29 §2.2. */
const WHERE_HINT = 'Wird bei jedem Lesen serverseitig angehängt und ist für Anwender unsichtbar.';
const FUNCTIONS_HINT = 'Ein Schlüsselwort wirkt auch als Teil eines Wortes.';
const EXPRESSION_WARNING_TITLE = 'Dieser Ausdruck wird so nicht wirken:';
const NEW_HINT = 'Felder und Gruppen lassen sich erst zuordnen, wenn die View gespeichert ist.';

const fetch = (id: number) => viewApi.getEntry(id);
const create = (model: ChinaAppSysView) => viewApi.create(model);
const put = (id: number, model: ChinaAppSysView) => viewApi.put(id, model);
const remove = (id: number) => viewApi.remove(id);

/** Leerer Text gehört als `null` in die nullbaren Spalten. */
function orNull(value: string): string | null {
  return value.trim() === '' ? null : value;
}

export function AdminViewForm(props: AdminViewFormProps) {
  // Nicht destrukturieren: `AdminChanges` deklariert die Meldung als Methode, ein
  // herausgelöster Verweis stößt @typescript-eslint/unbound-method an.
  const adminChanges = useAdminChanges();
  const [tab, setTab] = useState<TabId>('einstellungen');
  const form = useAdminForm({
    id: props.id,
    createEmpty: createChinaAppSysView,
    fetch,
    create,
    put,
    remove,
    deleteMessage:
      'Möchten Sie diese View wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.',
    deleteTitle: 'View löschen',
    onSave: async (id) => {
      // Name und Kategorie stehen in der Navigation.
      adminChanges.notifyNavigationChanged();
      await props.onSave(id);
    },
  });
  const { model } = form;

  const saved = model.Id > 0;
  // „Felder" und „Gruppen" sind gesperrt, solange die View keine Id hat — dann gehört der
  // Reiter „Einstellungen" nach vorn, sonst stünde kein Inhalt da.
  const active: TabId = saved ? tab : 'einstellungen';

  const functions = parseAdditionalFunctions(model.AdditionalFunctions);
  const orderWarnings = checkDefaultOrder(model.DefaultOrder, model.Entity);
  const filterWarnings = checkDefaultFilter(model.DefaultFilter, model.Entity);

  const setFunction = (keyword: AdditionalFunction, checked: boolean) => {
    const next = checked
      ? [...functions.active, keyword]
      : functions.active.filter((entry) => entry !== keyword);
    form.setField(
      'AdditionalFunctions',
      buildAdditionalFunctions({ active: next, rest: functions.rest }),
    );
  };

  const tabs: TabItem[] = [
    { id: 'einstellungen', label: 'Einstellungen' },
    { id: 'felder', label: 'Felder', disabled: !saved },
    { id: 'gruppen', label: 'Gruppen', disabled: !saved },
  ];

  return (
    <AdminFormFrame
      title={model.ViewName ?? ''}
      showDelete={saved}
      saving={form.saving}
      isValid={form.isValid}
      dirty={form.dirty}
      onNew={form.onNew}
      onRefresh={() => form.reload()}
      onDelete={form.onDelete}
      onSave={form.onSave}
    >
      <Tabs
        id={TABS_ID}
        items={tabs}
        value={active}
        ariaLabel="Bereiche der View"
        stretch
        onChange={(id) => {
          setTab(id as TabId);
        }}
      />

      <TabPanel tabsId={TABS_ID} id="einstellungen" active={active === 'einstellungen'}>
        <div className="iwm-admin-form-grid">
          <TextBox
            value={model.ViewName}
            onChange={(value) => {
              form.setField('ViewName', value);
            }}
            label="Name"
          />
          <CategoryDropdown
            value={model.CategoryId}
            onChange={(value) => {
              form.setField('CategoryId', value);
            }}
          />
          <EnumDropdown<EntityTypes>
            value={model.Entity}
            // `@bind-Value` auf einem nicht nullbaren Enum: Leeren setzt `default(EntityTypes)`.
            onChange={(value) => {
              form.setField('Entity', value ?? EntityTypes.ChinaAppSysGroup);
            }}
            displayNames={entityTypeDisplayNames}
            label="Entität"
          />

          <div className="iwm-admin-field iwm-admin-field--wide">
            <TextBox
              value={model.CustomEndpoint}
              onChange={(value) => {
                form.setField('CustomEndpoint', orNull(value));
              }}
              label="Additional Where Query"
            />
            <p className="iwm-admin-field-hint">{WHERE_HINT}</p>
          </div>

          <fieldset className="iwm-admin-field iwm-admin-field--wide iwm-admin-fieldset">
            <legend className="iwm-admin-field-legend">Additional Functions</legend>
            <div className="iwm-admin-function-list">
              {ADDITIONAL_FUNCTIONS.map((keyword) => (
                <div key={keyword} className="iwm-admin-function">
                  <CheckBox
                    label={keyword}
                    checked={functions.active.includes(keyword)}
                    onChange={(checked) => {
                      setFunction(keyword, checked);
                    }}
                  />
                  <span className="iwm-admin-field-hint">{ADDITIONAL_FUNCTION_HINTS[keyword]}</span>
                </div>
              ))}
            </div>
            <TextBox
              value={functions.rest}
              onChange={(value) => {
                form.setField(
                  'AdditionalFunctions',
                  buildAdditionalFunctions({ active: functions.active, rest: value }),
                );
              }}
              label="Weitere Schlüsselwörter"
            />
            <p className="iwm-admin-field-hint">{FUNCTIONS_HINT}</p>
          </fieldset>

          <div className="iwm-admin-field iwm-admin-field--wide">
            <TextBox
              value={model.DefaultOrder}
              onChange={(value) => {
                form.setField('DefaultOrder', orNull(value));
              }}
              label="Default Sort Query"
              placeholder="Feld asc, Feld2 desc"
            />
            <AdminWarnings title={EXPRESSION_WARNING_TITLE} warnings={orderWarnings} />
          </div>

          <div className="iwm-admin-field iwm-admin-field--wide">
            <TextBox
              value={model.DefaultFilter}
              onChange={(value) => {
                form.setField('DefaultFilter', orNull(value));
              }}
              label="Default Filter Query"
              placeholder="Feld contains Wert && Feld2 equal true"
            />
            <AdminWarnings title={EXPRESSION_WARNING_TITLE} warnings={filterWarnings} />
          </div>

          {saved ? null : <AdminNotice>{NEW_HINT}</AdminNotice>}
        </div>
      </TabPanel>

      <TabPanel tabsId={TABS_ID} id="felder" active={active === 'felder'}>
        {saved ? <AdminViewFieldsList parentId={model.Id} entity={model.Entity} /> : null}
      </TabPanel>

      <TabPanel tabsId={TABS_ID} id="gruppen" active={active === 'gruppen'}>
        {saved ? <AdminViewGroupsList parentId={model.Id} /> : null}
      </TabPanel>
    </AdminFormFrame>
  );
}

export default AdminViewForm;
